import { useEffect, useMemo, useState } from "preact/hooks";
import type { AppSettings, Folder } from "./api";
import { fullDate, relativeTime, size } from "./format";
import { Icon, folderIcon } from "./icons";
import { type OfflineStatus, keepAttachments, keepFolder, offlineSetting, saveSettings, useOfflineStatus } from "./offline";
import type { FolderState } from "./store";
import { type FolderNode, folderTree } from "./tree";

interface Props {
  folders: Folder[];
  settings: AppSettings | null;
  online: boolean;
  onSaved: (settings: AppSettings) => void;
  onMenu: () => void;
  onError: (error: unknown) => void;
}

interface Storage {
  usage: number | null;
  quota: number | null;
  persisted: boolean | null;
}

async function storageInfo(): Promise<Storage> {
  const s = typeof navigator !== "undefined" ? navigator.storage : undefined;
  const [estimate, persisted] = await Promise.all([s?.estimate?.().catch(() => null) ?? null, s?.persisted?.().catch(() => null) ?? null]);
  return { usage: estimate?.usage ?? null, quota: estimate?.quota ?? null, persisted };
}

/** Which folders are kept on this device for reading offline, with or without attachments (stored on the server,
 * so a reinstalled app keeps the choice), how far the copy got and how much room it takes. */
export function OfflineSettings({ folders, settings, online, onSaved, onMenu, onError }: Props) {
  const status = useOfflineStatus();
  const [storage, setStorage] = useState<Storage>({ usage: null, quota: null, persisted: null });
  /** The choice being saved, shown at once (put back if saving fails). */
  const [pending, setPending] = useState<AppSettings | null>(null);
  const tree = useMemo(() => folderTree(folders), [folders]);

  // the room taken, again whenever the copy changes
  useEffect(() => {
    storageInfo().then(setStorage);
  }, [status.kept, status.syncing?.done === status.syncing?.total]);

  const save = async (next: AppSettings) => {
    setPending(next);
    try {
      onSaved(await saveSettings(next));
    } catch (e) {
      onError(e);
    } finally {
      setPending(null);
    }
  };

  const persist = async () => {
    try {
      await navigator.storage.persist();
    } finally {
      setStorage(await storageInfo());
    }
  };

  const current = pending ?? settings;
  const chosen = current?.offline_folders ?? [];
  const picked = folders.filter((f) => offlineSetting(current, f.name));
  const disabled = !online || pending !== null || !current;

  const rows = (nodes: FolderNode[]): preact.JSX.Element[] =>
    nodes.flatMap((n) => {
      const f = n.folder;
      const kept = f ? offlineSetting(current, f.name) : null;
      const state = status.folders.find((s) => s.name === f?.name);
      return [
        <li key={n.path} class="offline-folder" style={{ "--depth": n.depth }}>
          {f ? (
            <label class="check">
              <input type="checkbox" checked={kept !== null} disabled={disabled} onChange={(e) => save(keepFolder(current!, f.name, (e.target as HTMLInputElement).checked))} />
              <Icon name={folderIcon(n.path)} size={18} />
              <span class="folder-name">{n.label}</span>
              <span class="muted small">
                {f.messages.toLocaleString()} · {size(f.size_bytes)}
              </span>
            </label>
          ) : (
            <div class="check">
              <Icon name="folder" size={18} />
              <span class="folder-name muted">{n.label}</span>
            </div>
          )}
          {f && kept && (
            <div class="offline-folder-detail">
              <label class="check small">
                <input type="checkbox" checked={kept.attachments} disabled={disabled} onChange={(e) => save(keepAttachments(current!, f.name, (e.target as HTMLInputElement).checked))} />
                With attachments
              </label>
              <span class="muted small">{folderProgress(f.name, state, status)}</span>
            </div>
          )}
        </li>,
        ...rows(n.children),
      ];
    });

  return (
    <div class="offline-settings">
      <div class="toolbar">
        <button class="icon-button menu" title="Folders" onClick={onMenu}>
          <Icon name="menu" />
        </button>
        <h2 class="title">Offline</h2>
      </div>
      <div class="settings-body">
        <p class="muted">
          The folders chosen here are kept on this device, so they can be read and searched without a connection. They are brought up to date whenever they change.
          The choice is kept on the server, so it survives reinstalling the app.
        </p>
        {!online && <p class="warn-text">No connection: the choice can be changed once there is one.</p>}

        <section class="storage" aria-label="Storage">
          <div>
            {storage.usage !== null ? `Using ${size(storage.usage)}` : "Storage use unknown"}
            {storage.quota !== null && ` of ${size(storage.quota)} the browser allows`} · {status.kept.toLocaleString()} messages kept
          </div>
          {storage.persisted === false && (
            <div class="muted small">
              The browser may clear the copy when the device runs low on space (it is fetched again then).{" "}
              <button class="text-button" onClick={persist}>
                Ask to keep it
              </button>
            </div>
          )}
          {storage.persisted && <div class="muted small">Kept permanently: the browser does not clear it when space runs low.</div>}
          {status.keptFrom !== null && <div class="warn-text small">Storage is full: mail from {fullDate(status.keptFrom) || "the oldest messages"} and before is not kept.</div>}
          {status.error && <div class="warn-text small">{status.error}</div>}
        </section>

        <p class="muted small">
          {chosen.length === 0
            ? "No folders are kept offline."
            : `${picked.length} ${picked.length === 1 ? "folder" : "folders"}, ${picked.reduce((n, f) => n + f.messages, 0).toLocaleString()} messages, up to ${size(picked.reduce((n, f) => n + f.size_bytes, 0))} with attachments.`}
        </p>
        <ul class="offline-folders" role="list">
          {rows(tree)}
        </ul>
      </div>
    </div>
  );
}

function folderProgress(name: string, state: FolderState | undefined, status: OfflineStatus): string {
  const now = status.syncing;
  if (now?.folder === name) return `Updating ${now.what}: ${now.done.toLocaleString()} of ${now.total.toLocaleString()}`;
  if (!state) return "Waiting to be kept";
  if (!state.complete) return "Waiting to be updated";
  return `Kept · updated ${relativeTime(state.synced_at)}`;
}
